/* eslint-disable camelcase */
import React, { useContext, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { DateTime } from 'luxon';
import { when, reaction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import { AlertGroupProvider } from '@ole-ui/ole-ui-components';
import {
  Button,
  AlertVariant,
  AlertActionLink,
  Alert,
  Modal,
  ModalVariant,
  Text,
  Tooltip,
  Flex,
} from '@patternfly/react-core';
import { Table, Tbody, Tr, Td } from '@patternfly/react-table';
import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import { storesContext } from 'stores';
import {
  formatDateTime,
  formatDateTimeWithTimezoneAbbreviation,
} from 'services/UtilityService';
import { extractFromSlug } from 'helpers/utils';
import useUserTimezone from 'hooks/useUserTimezone';
import {
  LiveVirtualClass,
  LiveClassStatus,
  LiveClassTableStatus,
  EnhancedLiveClassOffering,
} from 'types/liveClass';
import { LIVE_CLASS_CANCELLATION_WINDOW } from 'config/constants';
import { LandingTab } from 'components/Landing/types';
import {
  PARTNER_LIVECLASS_SCHEDULE_USER_URL_KEY,
  PARTNER_LIVECLASS_SCHEDULE_ROUTE_NAME,
} from 'components/Partner/Enrollments/config';
import PartnerStudentsStore from 'components/Partner/Enrollments/store';
import LiveClassOfferingStore from '../store';
import ScheduleTable from './ScheduleTable';
import ScheduleTableSkeleton from './ScheduleTableSkeleton';
import NoClassState from './NoClassState';
import { formatDeliveryDayRange, getDeliveryDays } from './DeliveryDaysExpand';

const offeringLiveCLassList = () => {
  const { i18n, t } = useTranslation();
  const { userTimezone } = useUserTimezone();
  const {
    catalogStore,
    routerStore,
    uiStore,
    userStore,
    vocabularyStore,
    liveClassOfferingFilterStore,
    liveClassStore,
  } = useContext(storesContext);

  const locale = i18n.language;

  const liveClassOfferingStore = useLocalStore(
    () =>
      new LiveClassOfferingStore(liveClassOfferingFilterStore, liveClassStore),
  );

  const route = routerStore?.route as any;

  const { scheduledOfferingBySlug } = liveClassOfferingStore;

  const offeringSlug = route?.params.offeringslug;
  const partnerUserUuid =
    route?.params?.[PARTNER_LIVECLASS_SCHEDULE_USER_URL_KEY];
  const { addAlert, removeAlert } = AlertGroupProvider.useAlertGroupContext();

  const isQuotaReached =
    liveClassStore.isEnrolledQuotaReached ||
    liveClassStore.isCompletedPerSkuQuotaReached ||
    liveClassStore.isEnrolledPlusCompletedPerSkuQuotaReached;

  // eslint-disable-next-line @typescript-eslint/default-param-last
  const presetFilters = (clear = false, routerName) => {
    if (
      clear &&
      (routerName === 'liveclass:schedule' ||
        routerName === PARTNER_LIVECLASS_SCHEDULE_ROUTE_NAME)
    ) {
      liveClassOfferingFilterStore.clearFilters();
      liveClassOfferingFilterStore.getFilterInfo();
    }

    liveClassOfferingStore.initSelectedOfferingToEnroll();
    liveClassOfferingFilterStore.presetFilters({
      instructors: liveClassOfferingFilterStore
        .flattenFilters('instructors')
        .join(','),
    });
  };

  useEffect(() => {
    if (!offeringSlug) {
      return;
    }

    if (route?.name === PARTNER_LIVECLASS_SCHEDULE_ROUTE_NAME) {
      liveClassStore.setPartnerScheduleContext(partnerUserUuid ?? null);
    }

    const loadOfferings = async () => {
      await liveClassStore.getOfferingUpcomingLiveClass(offeringSlug);
      await liveClassStore.getOfferingEnrolledLiveClass(offeringSlug);

      if (liveClassOfferingStore.ready) {
        liveClassOfferingStore.offeringSlug = offeringSlug;
        liveClassStore.offeringSlug = offeringSlug;
        await liveClassOfferingFilterStore.getFilterInfo();
      }
    };

    loadOfferings();
  }, [offeringSlug, route?.name, partnerUserUuid]);

  useEffect(() => {
    const cancelWhen = when(
      () => {
        if (
          !liveClassStore.offeringLiveClassesLoaded ||
          !liveClassStore.offeringEnrolledLiveClassesLoaded ||
          !routerStore?.route?.name
        ) {
          return false;
        }
        return liveClassStore.isPartnerScheduling || catalogStore.loaded;
      },
      async () => {
        if (!offeringSlug) {
          return;
        }

        liveClassOfferingStore.offeringSlug = offeringSlug;
        liveClassStore.offeringSlug = offeringSlug;
        liveClassOfferingFilterStore.getFilterInfo();
        const { getVocabularyByNamespace } = vocabularyStore;
        liveClassOfferingStore.languages = await getVocabularyByNamespace(
          'instruction_languages',
          uiStore.currentLanguage || 'en-US',
        );
        liveClassOfferingStore.ready = true;
      },
    );

    // Update preset filters when route changes
    const disposeReaction = reaction(
      () =>
        catalogStore.loaded &&
        liveClassStore.offeringLiveClassesLoaded &&
        liveClassStore.offeringEnrolledLiveClassesLoaded &&
        routerStore?.route?.name,
      () => presetFilters(true, routerStore?.route?.name),
    );

    return () => {
      cancelWhen();
      disposeReaction();
    };
  }, []);

  const handleEnrollmentClass = () => {
    removeAlert('live-class-enrollment');
    liveClassOfferingStore.submitProgress = {};
  };

  useEffect(() => {
    if (Object.keys(liveClassOfferingStore.submitProgress).length === 0) return;

    if (!liveClassOfferingStore.submitProgress?.hasError) {
      if (liveClassStore.isPartnerScheduling) {
        addAlert({
          title: `${liveClassOfferingStore.submitProgress?.title}`,
          children: `${liveClassOfferingStore.submitProgress?.message}`,
          variant: AlertVariant.success,
          key: 'live-class-enrollment',
          timeout: 5000,
          className: 'live-class-enrollment-success-alert',
        });
        liveClassOfferingStore.submitProgress = {};
        return;
      }

      liveClassStore.enrollmentSuccessAlert = {
        title: liveClassOfferingStore.submitProgress?.title,
        message: liveClassOfferingStore.submitProgress?.message,
      };
      routerStore.navigate('courses:overview', {
        course: offeringSlug,
        tab: LandingTab.LiveClass,
      });
      return;
    }

    addAlert({
      title: `${liveClassOfferingStore.submitProgress?.title}`,
      children: `${liveClassOfferingStore.submitProgress?.message}`,
      variant: AlertVariant.danger,
      key: 'live-class-enrollment',
      className: 'live-class-enrollment_alert',
      actionLinks: (
        <>
          <AlertActionLink
            variant="link"
            onClick={() => handleEnrollmentClass()}
          >
            {t('Close')}
          </AlertActionLink>
        </>
      ),
    });
  }, [liveClassOfferingStore.submitProgress]);

  useEffect(() => {
    if (!liveClassOfferingStore.showSubscriptionExpiredModal) return;

    const { code } = extractFromSlug(offeringSlug);
    const expiryDate = (() => {
      const endTimeIso =
        liveClassOfferingStore.subscriptionExpiredEntitlementEndTime ||
        userStore.entitlementsLatestEndTime(code);
      if (!endTimeIso) return '';
      const parsed = DateTime.fromISO(endTimeIso);
      return parsed.isValid ? parsed.toFormat('yyyy/MM/dd') : '';
    })();

    const boldExpiryDate = <strong>{expiryDate}</strong>;

    const description = (
      <p>
        {liveClassStore.isPartnerScheduling ? (
          <>
            {t('The subscription of the selected user will expire on')}{' '}
            {boldExpiryDate}{' '}
            {t(
              'which is before the end date of this live class. Please choose another date from the schedule.',
            )}
          </>
        ) : (
          <>
            {t('Your subscription will expire on')} {boldExpiryDate}{' '}
            {t(
              'which is before the end date of this live class. Please renew your subscription to select this live class, or choose another date from the schedule.',
            )}
          </>
        )}
      </p>
    );

    addAlert({
      title: t('Subscription expire'),
      children: description,
      variant: AlertVariant.warning,
      key: `live-class-subscription-expired`,
      className: 'live-class-enrollment_alert',
      actionLinks: (
        <>
          <AlertActionLink
            variant="primary"
            onClick={() => removeAlert(`live-class-subscription-expired`)}
          >
            {t('Close')}
          </AlertActionLink>
        </>
      ),
    });
    liveClassOfferingStore.showSubscriptionExpiredModal = false;
    liveClassOfferingStore.subscriptionExpiredEntitlementEndTime = '';
    liveClassOfferingStore.selectedOfferingToEnroll = '';
  }, [liveClassOfferingStore.showSubscriptionExpiredModal]);

  const getOfferingStatus = (
    offering: LiveVirtualClass,
  ): LiveClassTableStatus | null => {
    const { enrolledOfferingId } = liveClassOfferingStore;
    if (enrolledOfferingId && enrolledOfferingId === offering?.offering_id) {
      return LiveClassTableStatus.Scheduled;
    }

    if (
      offering?.offering_id &&
      offering.offering_id in (liveClassStore.overlapEnrolledOfferingsMap || {})
    ) {
      return LiveClassTableStatus.Overlap;
    }

    const lmsSeatStatus = (offering?.lms_seat_status || '').toLowerCase();
    if (lmsSeatStatus === LiveClassStatus.Full) {
      return LiveClassTableStatus.Full;
    }

    if (lmsSeatStatus === LiveClassStatus.Available) {
      return LiveClassTableStatus.Available;
    }

    return null;
  };

  const getGuaranteedToRunLabel = (offering: LiveVirtualClass) => {
    if (
      (offering.status || '').toLowerCase() === LiveClassStatus.GuaranteedToRun
    )
      return t('Yes');

    return t('No');
  };

  const overlapEnrolledOfferings =
    liveClassStore.overlapEnrolledOfferingsMap || {};

  const [isScheduleConfirmationModalOpen, setIsScheduleConfirmationModalOpen] =
    React.useState(false);

  const handleScheduleConfirmationModalToggle = () => {
    setIsScheduleConfirmationModalOpen(!isScheduleConfirmationModalOpen);
  };

  const handleConfirmCancel = async (offeringId: string) => {
    removeAlert(`cancel-class-${offeringId}`);
    const result = await liveClassOfferingStore.cancelUser(t);

    if (!result.hasError) {
      if (!liveClassStore.isPartnerScheduling) {
        liveClassStore.enrollmentSuccessAlert = {
          title: result.title ?? '',
          message: result.message ?? '',
        };
        routerStore.navigate('courses:overview', {
          course: offeringSlug,
          tab: LandingTab.LiveClass,
        });
      }
      return;
    }

    addAlert({
      title: result.title,
      children: result.message,
      variant: AlertVariant.danger,
      key: 'live-class-cancel-error',
      className: 'live-class-enrollment_alert',
      actionLinks: (
        <AlertActionLink
          variant="link"
          onClick={() => removeAlert('live-class-cancel-error')}
        >
          {t('Close')}
        </AlertActionLink>
      ),
    });
  };

  const showCancelClassConfirmationAlert = () => {
    const { enrolledOffering } = liveClassOfferingStore;
    if (!enrolledOffering) {
      return;
    }

    const {
      offering_id: offeringId,
      offering_name: offeringName,
      start_time: startTime,
      timezone: offeringTimezone,
    } = enrolledOffering;
    const startTimeFormatted = formatDateTimeWithTimezoneAbbreviation(
      startTime,
      offeringTimezone || userTimezone,
    );
    addAlert({
      title: t('Cancel this class?'),
      children: (
        <>
          {t('You are about to cancel your booking to')}{' '}
          <strong>{offeringName}</strong>{' '}
          {t('scheduled for {{startTimeFormatted}}', { startTimeFormatted })}
        </>
      ),
      variant: AlertVariant.warning,
      key: `cancel-class-${offeringId}`,
      className:
        'live-class-enrollment_alert live-class-cancel-confirmation-alert',
      actionLinks: (
        <>
          <AlertActionLink
            onClick={() => handleConfirmCancel(offeringId)}
            variant="secondary"
          >
            {t('Cancel class')}
          </AlertActionLink>
          <AlertActionLink
            variant="link"
            onClick={() => removeAlert(`cancel-class-${offeringId}`)}
          >
            {t('Keep class')}
          </AlertActionLink>
        </>
      ),
    });
  };

  const renderScheduleConfirmationModal = () => {
    const modalTitle = t('Live virtual class scheduling confirmation');
    const toScheduleOffering =
      liveClassOfferingStore.selectedOfferingToSchedule;

    const startTime = toScheduleOffering?.start_time
      ? formatDateTime(
          toScheduleOffering.start_time,
          locale,
          'MMM D, YYYY, ddd h:mm A',
        )
      : '';
    const offeringName = toScheduleOffering?.offering_name || '';
    const isReschedule = liveClassOfferingStore.canShowRescheduleButton;
    const deliveryDays = toScheduleOffering
      ? getDeliveryDays(toScheduleOffering, userTimezone)
      : [];

    return (
      <Modal
        variant={ModalVariant.small}
        className="live-session-enrollment-confirmation-modal"
        title={modalTitle}
        isOpen={isScheduleConfirmationModalOpen}
        showClose={!liveClassOfferingStore.submitting}
        onClose={() => {
          if (!liveClassOfferingStore.submitting) {
            handleScheduleConfirmationModalToggle();
          }
        }}
      >
        <div>
          <p>
            <Trans
              values={{
                offeringName,
                startTime,
              }}
            >
              {
                'You are about to schedule <strong>{{offeringName}}</strong>, starting on <strong>{{startTime}}</strong>. This live virtual class has the following session days:'
              }
            </Trans>
          </p>

          {deliveryDays.length > 0 && (
            <Table
              aria-label={t('Delivery days')}
              variant="compact"
              className="live-session-enrollment-confirmation-modal__days"
            >
              <Tbody>
                {deliveryDays.map((day, index) => {
                  const dayLabel =
                    day.sessionName || `${t('Day')} ${index + 1}`;
                  const rangeLabel = formatDeliveryDayRange(day, userTimezone);

                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <Tr key={`${day.start.toMillis()}-${index}`}>
                      <Td width={20}>{dayLabel}</Td>
                      <Td>{rangeLabel}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}

          {liveClassOfferingStore.isSelectedOfferingInCancellationPeriod ? (
            <p>
              {t(
                'If you would like to reschedule or cancel this class, you must do so previous to {{days}} days before the class start date.',
                { days: LIVE_CLASS_CANCELLATION_WINDOW },
              )}
            </p>
          ) : (
            <p>
              {t(
                'You will not be able to reschedule or cancel this class once you confirm.',
              )}{' '}
            </p>
          )}
        </div>
        <div className="live-session-enrollment-confirmation-modal__button">
          <Button
            variant="primary"
            isLoading={liveClassOfferingStore.submitting}
            spinnerAriaValueText={t('Loading')}
            isDisabled={liveClassOfferingStore.submitting}
            onClick={async () => {
              await liveClassOfferingStore.enrollUser(t, isReschedule);
              handleScheduleConfirmationModalToggle();
            }}
          >
            {liveClassOfferingStore.submitting
              ? t('Scheduling...')
              : t('Confirm')}
          </Button>
          <Button
            key="cancel"
            variant="link"
            isDisabled={liveClassOfferingStore.submitting}
            onClick={handleScheduleConfirmationModalToggle}
          >
            {t('Cancel')}
          </Button>
        </div>
      </Modal>
    );
  };

  const onRowSelected = (offering: EnhancedLiveClassOffering) => {
    if (liveClassStore.isPartnerScheduleReadOnly) {
      return;
    }

    const disableDueToLimit =
      liveClassOfferingStore.canShowScheduleButton &&
      liveClassStore.isAnyQuotaReached;

    const isSessionFull =
      getOfferingStatus(offering) === LiveClassTableStatus.Full;

    const isOfferingInEnrollmentPeriod =
      offering.within_enrollment_period === true;

    if (disableDueToLimit) {
      return;
    }
    if (!isSessionFull && isOfferingInEnrollmentPeriod)
      liveClassOfferingStore.onSelectOfferingToSchedule(offering.offering_id);
    else if (!isOfferingInEnrollmentPeriod) {
      liveClassOfferingStore.subscriptionExpiredEntitlementEndTime =
        offering.enrollment_max_end_time ?? '';
      liveClassOfferingStore.showSubscriptionExpiredModal = true;
    }
  };

  const isRowDisabled = (offering: EnhancedLiveClassOffering) => {
    if (liveClassStore.isPartnerScheduleReadOnly) {
      return true;
    }

    const isSessionFull =
      getOfferingStatus(offering) === LiveClassTableStatus.Full;
    const disableDueToLimit =
      liveClassOfferingStore.canShowScheduleButton &&
      liveClassStore.isAnyQuotaReached;

    const isOfferingOverlap = offering.offering_id in overlapEnrolledOfferings;
    return (
      liveClassOfferingStore.enrolledOfferingId === offering.offering_id ||
      isSessionFull ||
      isOfferingOverlap ||
      disableDueToLimit
    );
  };

  const getDisabledRowTooltip = (offering: EnhancedLiveClassOffering) => {
    if (liveClassOfferingStore.enrolledOfferingId === offering.offering_id)
      return null;
    if (offering.offering_id in overlapEnrolledOfferings) return null;
    const disableDueToLimit =
      liveClassOfferingStore.canShowScheduleButton &&
      liveClassStore.isAnyQuotaReached;
    if (disableDueToLimit) {
      return liveClassStore.isPartnerScheduling
        ? t(
            'Scheduling limit reached. The selected user must cancel or attend a scheduled class to schedule another.',
          )
        : t(
            'Scheduling limit reached. Cancel or attend a scheduled class to schedule another.',
          );
    }
    return null;
  };

  const renderPartnerScheduleBlockAlert = () => {
    if (!liveClassStore.isPartnerScheduleReadOnly) {
      return null;
    }

    const reason = liveClassStore.partnerScheduleBlockReason;

    if (reason === 'different_subscription') {
      return (
        <Alert isInline variant="warning" title={t('Cannot manage class')}>
          {t(
            'Cannot change a class as student has already scheduled with a different subscription type',
          )}
        </Alert>
      );
    }

    if (reason === 'in_progress') {
      return (
        <Alert isInline variant="warning" title={t('Cannot manage class')}>
          <Trans
            i18nKey="Cannot cancel or re-schedule a class. It is only accepted <bold>up to {{LIVE_CLASS_CANCELLATION_WINDOW}} days before</bold> the class start date"
            components={{ bold: <b /> }}
            values={{ LIVE_CLASS_CANCELLATION_WINDOW }}
          />
        </Alert>
      );
    }

    const formattedDate = PartnerStudentsStore.formatScheduleAlertDate(
      liveClassStore.partnerScheduleBlockDateIso,
    );

    if (reason === 'expired') {
      return (
        <Alert isInline variant="warning" title={t('Cannot manage class')}>
          {t(
            'Cannot manage class for this user. The subscription of the selected user has expired on',
          )}{' '}
          <strong>{formattedDate}</strong>.
        </Alert>
      );
    }

    return (
      <Alert isInline variant="warning" title={t('Cannot manage class')}>
        {t(
          'Cannot manage class for this user. The subscription of the selected user will start on',
        )}{' '}
        <strong>{formattedDate}</strong>.
      </Alert>
    );
  };

  const renderLimitReachedAlert = () => {
    if (!liveClassOfferingStore.canShowScheduleButton) {
      return null;
    }

    if (!isQuotaReached) {
      return null;
    }

    if (liveClassStore.completedPerSkuQuotaAllowed === 0) {
      return (
        <Alert isInline variant="danger" title={t('Class cannot be scheduled')}>
          {liveClassStore.isPartnerScheduling
            ? t(
                "This course is not included in the selected user's subscription. You will not be able to schedule any of the live virtual classes for this user.",
              )
            : t(
                'This course is not included in your subscription. You will not be able to schedule any of the live virtual classes.',
              )}
        </Alert>
      );
    }

    return (
      <Alert isInline variant="warning" title={t('Scheduling limit reached')}>
        <ul>
          {liveClassStore.isEnrolledQuotaReached && (
            <li>
              {liveClassStore.isPartnerScheduling
                ? t(
                    'The scheduling limit for this user with this course has been reached. This live class cannot be scheduled.',
                    {
                      enrolledQuotaUsed: liveClassStore.enrolledQuotaUsed || 0,
                    },
                  )
                : t(
                    'You already have {{enrolledQuotaUsed}} Live virtual classes scheduled. You must attend or cancel one of those classes in order to schedule this class.',
                    {
                      enrolledQuotaUsed: liveClassStore.enrolledQuotaUsed || 0,
                    },
                  )}
            </li>
          )}
          {(liveClassStore.isCompletedPerSkuQuotaReached ||
            liveClassStore.isEnrolledPlusCompletedPerSkuQuotaReached) && (
            <li>
              {liveClassStore.isPartnerScheduling
                ? t(
                    'The scheduling limit for this user with this course has been reached. This live class cannot be scheduled.',
                  )
                : t(
                    'You already attended this live virtual class the maximum amount allowed by your subscription. You may not attend this class again during this subscription.',
                  )}
            </li>
          )}
        </ul>
      </Alert>
    );
  };

  if (!liveClassOfferingStore.ready) {
    return (
      <div className="live-class-schedule-container">
        <section className="schedule-list-container">
          <Text component="h4">
            <b>{t('Scheduling')}</b>
          </Text>
          <div>
            <ScheduleTableSkeleton />
          </div>
        </section>
      </div>
    );
  }

  const isNotSupportedBySubscription =
    liveClassOfferingStore.canShowScheduleButton &&
    isQuotaReached &&
    liveClassStore.completedPerSkuQuotaAllowed === 0;

  return (
    <div className="live-class-schedule-container">
      {renderScheduleConfirmationModal()}

      <div className="live-class-alerts-container">
        {renderPartnerScheduleBlockAlert()}
      </div>

      {liveClassStore.scheduledOfferingLiveClasses.length > 0 ? (
        <section className="schedule-list-container">
          <Text component="h4">
            <b>{t('Scheduling')}</b>
          </Text>
          <div className="live-class-alerts-container">
            {renderLimitReachedAlert()}
          </div>
          <div>
            <ScheduleTable
              key={`schedule-table-${
                liveClassOfferingStore.enrolledOfferingId || 'none'
              }`}
              data={scheduledOfferingBySlug}
              languages={liveClassOfferingStore.languages}
              getOfferingStatus={getOfferingStatus}
              getGuaranteedToRunLabel={getGuaranteedToRunLabel}
              onRowSelected={onRowSelected}
              isRowDisabled={isRowDisabled}
              isNotSupportedBySubscription={isNotSupportedBySubscription}
              overlapEnrolledOfferings={overlapEnrolledOfferings}
              enrolledOfferingId={liveClassOfferingStore.enrolledOfferingId}
              getDisabledRowTooltip={getDisabledRowTooltip}
              isPartnerSchedule={liveClassStore.isPartnerScheduling}
            />
          </div>
          <div className="schedule-buttons">
            {liveClassOfferingStore.canShowScheduleButton && (
              <Button
                variant="primary"
                isDisabled={!liveClassOfferingStore.isAllowToSchedule}
                type="submit"
                onClick={() => handleScheduleConfirmationModalToggle()}
                data-analytics-id="schedule-btn-session-scheduler-view-lp"
                className="schedule-reschedule-btn"
              >
                {t('Schedule')}
              </Button>
            )}
            {liveClassOfferingStore.canShowRescheduleButton &&
              (liveClassOfferingStore.isCurrentlyScheduledClassTooSoon ? (
                <Tooltip
                  content={
                    <div>
                      <p>
                        {t(
                          liveClassStore.isPartnerScheduling
                            ? 'Reschedule is not available because the scheduled class is within {{days}} days of its start date.'
                            : 'Reschedule is not available because your scheduled class is within {{days}} days of its start date.',
                          { days: LIVE_CLASS_CANCELLATION_WINDOW },
                        )}
                      </p>
                    </div>
                  }
                >
                  <span style={{ display: 'inline-block' }}>
                    <Button
                      variant="primary"
                      isDisabled={!liveClassOfferingStore.isAllowToReSchedule}
                      type="submit"
                      onClick={() => handleScheduleConfirmationModalToggle()}
                      data-analytics-id="schedule-btn-session-scheduler-view-lp"
                      className="schedule-reschedule-btn"
                    >
                      <Flex
                        alignItems={{ default: 'alignItemsCenter' }}
                        gap={{ default: 'gapSm' }}
                        flexWrap={{ default: 'nowrap' }}
                      >
                        {t('Reschedule')}
                      </Flex>
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  variant="primary"
                  isDisabled={!liveClassOfferingStore.isAllowToReSchedule}
                  type="submit"
                  onClick={() => handleScheduleConfirmationModalToggle()}
                  data-analytics-id="schedule-btn-session-scheduler-view-lp"
                  className="schedule-reschedule-btn"
                >
                  {t('Reschedule')}
                </Button>
              ))}
            {liveClassOfferingStore.canShowCancelButton &&
              (liveClassOfferingStore.isCurrentlyScheduledClassTooSoon ? (
                <Tooltip
                  content={
                    <div>
                      <p>
                        {t(
                          'Live classes can only be canceled more than {{days}} days before the start date.',
                          { days: LIVE_CLASS_CANCELLATION_WINDOW },
                        )}
                      </p>
                    </div>
                  }
                >
                  <span style={{ display: 'inline-block' }}>
                    <Button
                      variant="danger"
                      isDisabled
                      className="schedule-reschedule-btn"
                      data-analytics-id="cancel-class-btn-session-scheduler-view-lp"
                    >
                      {t('Cancel class')}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  variant="danger"
                  isDisabled={!liveClassOfferingStore.isAllowToCancel}
                  onClick={showCancelClassConfirmationAlert}
                  className="schedule-reschedule-btn"
                  data-analytics-id="cancel-class-btn-session-scheduler-view-lp"
                >
                  {t('Cancel class')}
                </Button>
              ))}
          </div>
        </section>
      ) : (
        <NoClassState />
      )}
    </div>
  );
};

export default withErrorBoundary(
  observer(offeringLiveCLassList),
  GenericViewError,
  handleError,
);
